import React, { useState } from "react"
import { Checkbox, Box } from "@material-ui/core"
import { KeyboardArrowDown as ChevronIcon, WarningOutlined as WarningIcon } from "@material-ui/icons"
import { Text } from "letalkui"

import { ChatBotQueueCode } from "@/protocols/chatBot"

import { QueueGroup, isQueueActive } from "@/pages/Admin/ChatBot/ChatBotList/ChatBotConfig/queuesConstants"

import useStyles from "@/pages/Admin/ChatBot/ChatBotList/ChatBotConfig/QueuesSection/styles"

type QueuesSectionProps = {
	allowedQueues: Partial<Record<ChatBotQueueCode, boolean>> | undefined
	groups: QueueGroup[]
	onToggleQueue: (code: ChatBotQueueCode) => void
	onToggleGroup: (group: QueueGroup, isAllOn: boolean) => void
}

const QueuesSection: React.FC<QueuesSectionProps> = (props) => {
	const {
		allowedQueues,
		groups,
		onToggleQueue,
		onToggleGroup
	} = props

	const classes = useStyles()

	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
		"Em atendimento": true
	})

	const totalActive = groups.reduce((total, group) => {
		return total + group.queues.filter(queue => isQueueActive(allowedQueues, queue.code)).length
	}, 0)

	const showBanner = isQueueActive(allowedQueues, "on-going") || isQueueActive(allowedQueues, "snooze")

	const handleToggleExpand = (groupLabel: string) => {
		setExpandedGroups(prev => ({
			...prev,
			[groupLabel]: !prev[groupLabel]
		}))
	}

	const handleGroupCheckboxClick = (group: QueueGroup, event: React.MouseEvent) => {
		event.stopPropagation()
		const isAllOn = group.queues.every(queue => isQueueActive(allowedQueues, queue.code))
		onToggleGroup(group, isAllOn)
	}

	return (
		<Box className={classes.card}>
			<Box className={classes.cardHeader}>
				<Box className={classes.cardHeaderText}>
					<Text.Body type="small1" fontWeight="bold" color="#2c2937">
						Filas
					</Text.Body>
					<Text.Body type="smaller1" fontWeight="regular" color="#6a6578">
						Ative as filas em que este bot pode interromper a conversa. Se a conversa estiver em uma fila desativada, o bot não é iniciado e ela continua onde está.
					</Text.Body>
				</Box>
				<Box className={classes.activeBadge}>
					<Text.Body type="smaller2" fontWeight="bold" color="#5b3fd1">
						{totalActive} ativas
					</Text.Body>
				</Box>
			</Box>

			<Box className={classes.groupsList}>
				{groups.map(group => {
					const groupQueues = group.queues
					const activeCount = groupQueues.filter(queue => isQueueActive(allowedQueues, queue.code)).length
					const totalCount = groupQueues.length
					const isAllOn = groupQueues.every(queue => isQueueActive(allowedQueues, queue.code))
					const isExpanded = !!expandedGroups[group.label]

					return (
						<Box key={group.label} className={classes.groupCard}>
							<Box
								className={classes.groupHeader}
								onClick={() => handleToggleExpand(group.label)}
								role="button"
								aria-expanded={isExpanded}
							>
								<Box className={classes.groupLabelColumn}>
									<Text.Body type="small1" fontWeight="bold" color="#2c2937">
										{group.label}
									</Text.Body>
									<Text.Body type="smaller2" fontWeight="regular" color="#6a6578">
										{group.summary}
									</Text.Body>
								</Box>
								<Box className={classes.countBadge}>
									<Text.Body type="smaller2" fontWeight="bold" color="#6a6578">
										{activeCount}/{totalCount}
									</Text.Body>
								</Box>
								<Checkbox
									checked={isAllOn}
									onClick={(event) => handleGroupCheckboxClick(group, event)}
									classes={{
										root: classes.checkboxRoot
									}}
									icon={<Box component="span" className={`${classes.checkbox} ${classes.checkboxUnchecked}`} />}
									checkedIcon={<Box component="span" className={`${classes.checkbox} ${classes.checkboxChecked}`}>
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
											<path d="M5 12l5 5 9-10" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</Box>}
								/>
								<Box
									component="span"
									className={`${classes.chevron} ${isExpanded ? classes.chevronExpanded : ""}`}
								>
									<ChevronIcon fontSize="inherit" />
								</Box>
							</Box>

							{isExpanded && (
								<Box className={classes.groupBody}>
									<Box className={classes.queueList}>
										{groupQueues.map(queue => {
											const isChecked = isQueueActive(allowedQueues, queue.code)
											return (
												<Box
													key={queue.code}
													className={classes.queueRow}
													onClick={() => onToggleQueue(queue.code)}
												>
													<Box className={classes.queueInfo}>
														<Box className={classes.queueLabelRow}>
															<Text.Body type="small2" fontWeight="bold" color="#2c2937">
																{queue.label}
															</Text.Body>
															<Box className={classes.queueCodeBadge}>
																<Text.Body type="smaller3" fontWeight="bold" color="#7a7588" sx={{ fontFamily: "monospace" }}>
																	{queue.code}
																</Text.Body>
															</Box>
														</Box>
														<Text.Body type="smaller2" fontWeight="regular" color="#7a7588">
															{queue.description}
														</Text.Body>
													</Box>
													<Checkbox
														checked={isChecked}
														classes={{
															root: classes.checkboxRoot
														}}
														icon={<Box component="span" className={`${classes.checkbox} ${classes.checkboxUnchecked}`} />}
														checkedIcon={<Box component="span" className={`${classes.checkbox} ${classes.checkboxChecked}`}>
															<svg width="12" height="12" viewBox="0 0 24 24" fill="none">
																<path d="M5 12l5 5 9-10" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
															</svg>
														</Box>}
													/>
												</Box>
											)
										})}
									</Box>
								</Box>
							)}
						</Box>
					)
				})}
			</Box>

			{showBanner && (
				<Box className={classes.warningBanner}>
					<WarningIcon className={classes.warningIcon} />
					<Text.Body type="smaller1" fontWeight="regular" color="#7a5c00">
						Com filas de atendimento ativas, conversas que já estão com um atendente podem ser retiradas dele e movidas para a fila do bot.
					</Text.Body>
				</Box>
			)}
		</Box>
	)
}

export default QueuesSection
